/**
 * @see https://umijs.org/docs/max/access#access
 * */
export default function access(
  initialState: { currentUser?: OA.CurrentUser } | undefined,
) {
  const { currentUser } = initialState ?? {};

  /**
   * 检查用户是否为系统管理员
   */
  const isSystemAdmin = (): boolean => {
    return currentUser?.user_type === 'system_admin';
  };

  /**
   * 检查用户是否有指定权限
   * 系统管理员拥有所有权限
   */
  const hasPermission = (permission: string): boolean => {
    // 系统管理员拥有所有权限
    if (isSystemAdmin()) {
      return true;
    }

    if (!currentUser || !currentUser.permissions) {
      return false;
    }
    return currentUser.permissions.includes(permission);
  };

  /**
   * 检查用户是否有指定角色
   * 系统管理员拥有所有角色
   */
  const hasRole = (role: string): boolean => {
    // 系统管理员拥有所有角色
    if (isSystemAdmin()) {
      return true;
    }

    if (!currentUser || !currentUser.roles) {
      return false;
    }
    return currentUser.roles.includes(role);
  };

  /**
   * 检查用户是否为部门负责人
   */
  const isDepartmentLeader = (): boolean => {
    // 系统管理员默认拥有部门负责人权限
    return currentUser?.is_department_leader ?? false;
  };

  return {

    // 是否是管理员
    canAdmin: currentUser && currentUser.user_type === 'system_admin',

    // 权限检查函数
    hasPermission,

    // 角色检查函数
    hasRole,

    // 是否是部门负责人
    isDepartmentLeader,

    // ==================== 路由级别权限 ====================
    // 组织架构模块 - 需要查看部门权限
    canAccessOrganization: hasPermission('department:view'),

    // 用户管理模块 - 需要查看用户权限
    canAccessUser: hasPermission('user:view'),

    // 权限管理模块 - 需要查看权限
    canAccessSystem: hasPermission('system:view'),

    // 假期管理模块 - 需要查看假期权限
    canAccessVacation: hasPermission('vacation:view'),

    // 审批模块 - 需要查看审批权限
    canAccessApproval: hasPermission('approval:view'),

    // 审批模板管理 - 需要管理审批模板权限
    canAccessApprovalTemplates: hasPermission('approval:template:view'),

    // 提交审批 - 需要提交审批权限
    canAccessApprovalSubmit: hasPermission('approval:submit'),

    // 部门审批列表 - 需要部门负责人权限
    canAccessDepartmentApproval: isDepartmentLeader(),

    // 员工档案模块 - 需要查看员工档案权限
    canAccessProfile: hasPermission('profile:view'),

    // ==================== 操作级别权限 ====================
    // 用户管理相关权限
    canCreateUser: hasPermission('user:create'),
    canUpdateUser: hasPermission('user:update'),
    canDeleteUser: hasPermission('user:delete'),
    canViewUser: hasPermission('user:view'),

    // 部门管理相关权限
    canCreateDepartment: hasPermission('department:create'),
    canUpdateDepartment: hasPermission('department:update'),
    canDeleteDepartment: hasPermission('department:delete'),
    canViewDepartment: hasPermission('department:view'),

    // 角色管理相关权限
    canCreateRole: hasPermission('role:create'),
    canUpdateRole: hasPermission('role:update'),
    canDeleteRole: hasPermission('role:delete'),
    canViewRole: hasPermission('role:view'),

    // 权限管理相关权限
    canCreatePermission: hasPermission('permission:create'),
    canUpdatePermission: hasPermission('permission:update'),
    canDeletePermission: hasPermission('permission:delete'),
    canViewPermission: hasPermission('permission:view'),

    // 假期管理相关权限
    canCreateVacation: hasPermission('vacation:create'),
    canUpdateVacation: hasPermission('vacation:update'),
    canDeleteVacation: hasPermission('vacation:delete'),
    canViewVacation: hasPermission('vacation:view'),

    // 审批相关权限
    canViewApproval: hasPermission('approval:view'),
    canSubmitApproval: hasPermission('approval:submit'),
    canApproveApproval: hasPermission('approval:approve'),
    canRejectApproval: hasPermission('approval:reject'),
    canCancelApproval: hasPermission('approval:cancel'),
    canViewApprovalList: hasPermission('approvallist:view'),
    canRevokeApproval: hasPermission('approval:revoke'),

    // 审批模板相关权限
    canCreateApprovalTemplate: hasPermission('approval:template:create'),
    canUpdateApprovalTemplate: hasPermission('approval:template:update'),
    canDeleteApprovalTemplate: hasPermission('approval:template:delete'),
    canViewApprovalTemplate: hasPermission('approval:template:view'),

    // 员工档案相关权限
    canCreateProfile: hasPermission('profile:create'),
    canEditProfile: hasPermission('profile:edit'),
    canDeleteProfile: hasPermission('profile:delete'),
    canViewProfile: hasPermission('profile:view'),

    // 公车管理模块 - 需要查看公车管理权限
    canAccessVehicle: hasPermission('vehicle:access'),

    // 发文管理模块 - 需要查看发文管理权限
    canAccessDocument: hasPermission('document:view'),

    // 发文管理相关权限
    canViewDocument: hasPermission('document:view'),
    canExportDocument: hasPermission('document:export'),
    canConfigDocumentNumber: hasPermission('document:config'),

    // 收文传阅模块 - 需要查看收文传阅权限
    canAccessIncomingDocument: hasPermission('incoming_document:view'),

    // 收文传阅相关权限
    canViewIncomingDocument: hasPermission('incoming_document:view'),
    canCirculateIncomingDocument: hasPermission('incoming_document:circulate'),
    canViewCirculationRecords: hasPermission('incoming_document:view_circulation'),

    // 排班管理模块 - 需要查看排班权限
    canAccessShift: hasPermission('shift:view'),

    // 排班管理相关权限
    canViewShift: hasPermission('shift:view'),
    canCreateShift: hasPermission('shift:create'),
    canDeleteShift: hasPermission('shift:delete'),

    // 资产管理模块 - 需要查看资产权限
    canAccessAsset: hasPermission('asset:view'),

    // 资产管理相关权限
    canViewAsset: hasPermission('asset:view'),
    canCreateAsset: hasPermission('asset:create'),
    canEditAsset: hasPermission('asset:edit'),
    canDeleteAsset: hasPermission('asset:delete'),

    // 合同管理模块 - 需要查看合同权限
    canAccessContract: hasPermission('contract:view'),

    // 合同管理相关权限
    canViewContract: hasPermission('contract:view'),
    canCreateContract: hasPermission('contract:create'),
    canEditContract: hasPermission('contract:edit'),
    canDeleteContract: hasPermission('contract:delete'),
    canManageContractAdmin: hasPermission('contract:adminmanage'),
    // 文书档案管理模块 - 需要查看文书档案权限
    canAccessArchive: hasPermission('archive:view'),

    // 文书档案管理相关权限
    canViewArchive: hasPermission('archive:view'),
    canAccessAllArchive: hasPermission('archive:accessall'),
    canCreateArchive: hasPermission('archive:create'),
    canUpdateArchive: hasPermission('archive:update'),
    canDeleteArchive: hasPermission('archive:delete'),
    canManageArchiveAdmin: hasPermission('archive:adminmanage'),
    canConfigArchiveNumber: hasPermission('archive:config'),

    // 房产管理模块 - 需要查看房产权限
    canAccessProperty: hasPermission('property:view'),

    // 房产管理相关权限
    canViewProperty: hasPermission('property:view'),
    canCreateProperty: hasPermission('property:create'),
    canEditProperty: hasPermission('property:edit'),
    canDeleteProperty: hasPermission('property:delete'),

    // 信息发布模块 - 需要查看信息发布权限
    canAccessPublish: hasPermission('publish:view'),

    // 信息发布相关权限
    canViewPublish: hasPermission('publish:view'),
  };
}
